<template>
  <div>
    <div class="home-grid">
      <div class="infographic">
        <ul class="infographic-tabs">
          <li :class="{ active: currentChart == 'grades' }">
            <a href="#" @click.prevent="currentChart = 'grades'">Promedio semestral</a>
          </li>
        </ul>
        <div class="chart">
          <grades-chart
            v-if="currentChart == 'grades'"
            :styles="chartStyles"
            :student-id="studentId"
          ></grades-chart>
          <assistance-chart
            v-if="currentChart == 'assistance'"
            :styles="chartStyles"
          ></assistance-chart>
        </div>
      </div>

      <div class="main-box">
        <div class="title">
          <a class="title-link" href="#" @click.prevent="navigateTo('app.profile.alerts')">
            <span>Alertas ({{ studentProfile.activeAlerts }})</span>
            <next-arrow-icon></next-arrow-icon>
          </a>
        </div>
        <div class="body is-hidden-mobile">
          <h4 class="is-marginless has-text-weight-medium body__title">FACTORES DE RIESGO</h4>
          <div class="risk-factors">
            <div v-for="r in riskFactors" v-bind:key="r.code" class="risk-factors-item">
              <span class="risk-factor-indicator" :class="{ 'is-at-risk': r.on }"></span>
              <span class="risk-factor-name fs-14 is-lowercase">{{ r.name }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="main-box">
        <a class="title-link" href="#" @click.prevent="navigateTo('app.profile.attentions')">
          <span>Atenciones ({{ studentProfile.attentionStats.total }})</span>
          <next-arrow-icon></next-arrow-icon>
        </a>
        <div class="body is-hidden-mobile center-content">
          <div class="metric-list">
            <div class="metric-list-item">
              <span class="metric-value">{{ studentProfile.attentionStats.pending }}</span>
              <span class="metric-concept">Pendientes</span>
            </div>
            <div class="metric-list-item">
              <span class="metric-value">{{ studentProfile.attentionStats.done }}</span>
              <span class="metric-concept">Realizadas</span>
            </div>
            <div class="metric-list-item">
              <span class="metric-value">
                {{ studentProfile.attentionStats.participation | percentage }}
              </span>
              <span class="metric-concept">Participación</span>
            </div>
          </div>
        </div>
      </div>

      <div class="main-box">
        <a class="title-link" href="#" @click.prevent="navigateTo('app.profile.grades')">
          <span>Notas</span>
          <next-arrow-icon></next-arrow-icon>
        </a>
        <div class="body is-hidden-mobile center-content">
          <div class="metric-list">
            <div class="metric-list-item">
              <span class="metric-value">{{ studentProfile.student.currentLevel }}</span>
              <span class="metric-concept">Nivel</span>
            </div>
            <div class="metric-list-item">
              <span class="metric-value">
                {{
                  studentProfile.student.currentCurricularProgress | percentage(this.institution)
                }}
              </span>
              <span class="metric-concept">Créditos</span>
            </div>
            <div class="metric-list-item">
              <span class="metric-value">{{ average | humanizeFloat }}</span>
              <span class="metric-concept">Promedio</span>
            </div>
          </div>
        </div>
      </div>
      <div class="main-box">
        <a class="title-link" href="#" @click.prevent="navigateTo('app.profile.comments')">
          <span>Comentarios ({{ studentProfile.totalComments }})</span>
          <next-arrow-icon></next-arrow-icon>
        </a>
        <div v-if="studentProfile.totalComments !== 0" class="body is-hidden-mobile">
          <div class="comments-list">
            <div v-for="c in studentProfile.latestComments" :key="c.id" class="comment-item">
              <div class="comment-meta has-text-weight-semibold">
                {{ c.author.name }}, {{ c.createdAt | humanizeDate }}
              </div>
              <p class="comment-text">{{ c.comment | truncate(MAX_LENGTH_COMMENTS_RESUME) }}</p>
              <a
                v-if="c.comment.length > MAX_LENGTH_COMMENTS_RESUME"
                class="see-more"
                href="#"
                @click.prevent="navigateTo('app.profile.comments')"
                >ver más</a
              >
            </div>
          </div>
        </div>
        <p v-else class="body is-hidden-mobile center-content">No hay comentarios registrados.</p>
      </div>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag"
import moment from "moment"
import PROFILE_DETAIL from "./graphql/Profile.gql"
import NextArrowIcon from "@/components/_base/NextArrowIconNeutral.vue"
import GradesChart from "./GradesChart.vue"
import AssistanceChart from "./AssistanceChart.vue"

export default {
  name: "ProfileHome",
  props: {
    studentId: {
      type: String
    },
    user: {
      type: Object
    }
  },
  data() {
    return {
      MAX_LENGTH_COMMENTS_RESUME: 90,
      studentProfile: {
        student: {
          currentRiskFactors: [],
          currentLevel: null,
          currentCurricularProgress: 0.0,
          gradeAverage: {
            grade: null
          }
        },
        activeAlerts: null,
        attentionStats: {
          done: 0,
          total: 0,
          participation: 0
        },
        latestComments: []
      },
      currentChart: "grades",
      institution: this.user.institution_code
    }
  },
  apollo: {
    studentProfile: {
      query: PROFILE_DETAIL,
      variables() {
        return {
          studentId: this.studentId
        }
      }
    }
  },
  computed: {
    chartStyles() {
      return {
        position: "relative",
        // width: "400px",
        height: "145px",
        overflow: "hidden"
      }
    },

    average() {
      return this.studentProfile.student.gradeAverage.grade || 0.0
    },

    riskFactors() {
      const ret = [
        {
          code: "ACADEMIC",
          name: "Académico",
          on: false
        },
        {
          code: "VOCATIONAL",
          name: "Vocacional",
          on: false
        },
        {
          code: "ECONOMIC",
          name: "Económico",
          on: false
        },
        {
          code: "INSTITUTIONAL",
          name: "Institucional",
          on: false
        },
        {
          code: "PERSONAL",
          name: "Personal",
          on: false
        },
        {
          code: "OTHER",
          name: "Otros",
          on: false
        }
      ]

      // add a custom risk factor only for UAM
      if (this.institution === "uam.co") {
        ret.push({
          code: "VBG",
          name: "VBG",
          on: false
        })
      }

      // Activate the current factors
      ret.forEach(item => {
        item.on = this.studentProfile.student.currentRiskFactors.includes(item.code)
      })

      return ret
    }
  },
  methods: {
    navigateTo(route) {
      console.log(`Navigating to...${route}`)
      this.$emit("hello", route)
    }
  },
  components: {
    "grades-chart": GradesChart,
    "assistance-chart": AssistanceChart,
    "next-arrow-icon": NextArrowIcon
  },
  filters: {
    humanizeFloat(value) {
      if (value == null || Number.isNaN(value)) {
        return "-"
      }
      return (Math.round(value * 10) / 10).toFixed(1)
    },
    percentage(value, institution) {
      if (value == null || Number.isNaN(value)) {
        return "-"
      }

      if (institution == "uam.co") {
        return value
      } else {
        return (value * 100).toFixed(0) + "%"
      }
    },
    humanizeDate(value) {
      const m = moment(value)
      return m.fromNow()
    },
    truncate(text, stop) {
      return text.slice(0, stop) + (text.length > stop ? "..." : "")
    }
  }
}
</script>

<style scoped>
.home-grid {
  display: flex;
  flex-direction: column;
}

/* todo: importar variables aquí */
@media screen and (min-width: 769px) {
  .home-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 240px 200px 1fr;
    grid-column-gap: 8px;
    grid-row-gap: 6px;
  }
}

.infographic {
  background: white;
  margin-bottom: 5px;
  overflow: auto;
  grid-column: 1 / span 2;
  border-radius: 8px;
}

.main-box {
  box-sizing: border-box;
  background: white;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
}

.main-box .title-link {
  font-family: Roboto;
  color: var(--color-primary-60);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  font-size: 22px;
  line-height: 26px;
  padding: 15px;
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid #bdbdbd;
}

.main-box .title-link:hover {
  text-decoration: underline;
}

.main-box .title-link:active {
  color: white;
  background-color: var(--color-primary-60);
}

.center-content {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 !important;
}

.main-box .body {
  flex-grow: 1;
  min-height: 120px;
  padding: 20px 30px;
  max-height: 200px;
  overflow: auto;
}

.main-box .body h4 {
  font-weight: bold;
  color: var(--color-neutral-90);
  font-size: 14px;
  line-height: 14px;
  font-family: Roboto;
}

.risk-factors {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 10px;
  padding-left: 10px;
}

.risk-factors-item {
  flex-basis: 50%;
  margin: 3px 0;
}

.risk-factor-name {
  font-size: 14px;
  line-height: 18px;
  font-family: Roboto;
  color: var(--color-neutral-90);
}

.risk-factor-indicator {
  display: inline-block;
  margin-right: 5px;
  width: 10px;
  height: 10px;
  background: #e0e0e0;
  border-radius: 25%;
}

.risk-factor-indicator.is-at-risk {
  background: var(--color-error-50);
}

/* METRICS */

.metric-list {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  width: 100%;
}

.metric-list-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.metric-list-item .metric-value {
  font-family: Roboto;
  font-size: 40px;
  line-height: 32px;
  font-weight: medium;
  margin-bottom: 5px;
  color: var(--color-neutral-70);
}
.metric-list-item .metric-concept {
  font-size: 14px;
  line-height: 18px;
  font-family: Roboto;
  color: var(--color-neutral-90);
}

/* COMMENTS */
.comments-list {
  font-size: 14px;
}

.comments-list .comment-meta,
.comments-list .comment-text {
  font-family: Roboto;
  font-size: 14px;
  line-height: 18px;
  color: var(--color-neutral-90);
}

.comment-item {
  margin: 10px 0;
}

.comment-text {
  display: inline;
  margin: 0;
}

.see-more {
  color: #333;
}

.infographic-tabs {
  list-style: none;
  margin: 0;
  padding: 0;
  padding: 15px 0px 5px;
  margin: 0 50px 25px 56px;
  border-bottom: 1px solid var(--color-neutral-30);
}

.infographic-tabs li {
  display: inline;
  padding: 5px 10px;
  margin: 0 0px;
  font-size: 14px;
  line-height: 14px;
  color: var(--color-neutral-70);
  font-family: Roboto;
}

.infographic-tabs li a {
  color: inherit;
  text-decoration: none;
}

.infographic-tabs li.active {
  border-bottom: 1px solid var(--color-neutral-70);
}
.infographic-tabs li.active a {
  font-weight: bold;
  color: var(--color-neutral-90) !important;
}
</style>
